import { getStoredAuthToken, getStoredRefreshToken, storeTokens, clearStoredAuth } from './utils/authToken';

export const API_BASE_URL = (import.meta.env.VITE_API_URL || 'https://vms.elsnerdev.co').replace(/\/$/, '');

export const apiUrl = (path) => {
  if (!path) return API_BASE_URL;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

// ─── Refresh-token machinery ───────────────────────────────────────────────────
let isRefreshing = false;
let refreshPromise = null;
let failedQueue = []; // requests waiting for the refresh to finish

/**
 * Process queued requests after refresh completes.
 * If refresh succeeded, resolve them with the new token.
 * If refresh failed, reject them so callers can handle it.
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

/**
 * Silently refresh the access token using the stored refresh token.
 * Returns the new access token on success, or null on failure.
 */
const tryRefreshToken = async () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  try {
    const resp = await fetch(apiUrl('/api/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });

    if (!resp.ok) return null;

    const data = await resp.json();
    if (data.token) {
      // Store both the new access token AND the rotated refresh token
      storeTokens(data.token, data.refreshToken);
      return data.token;
    }
    return null;
  } catch {
    // Network error — don't treat as auth failure
    return null;
  }
};

// ─── Proactive refresh ─────────────────────────────────────────────────────────
// Decode JWT expiry and schedule a refresh 5 minutes before it expires.
let proactiveRefreshTimer = null;

const decodeTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // milliseconds
  } catch {
    return null;
  }
};

const scheduleProactiveRefresh = (token) => {
  if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);

  const exp = decodeTokenExpiry(token);
  if (!exp) return;

  // Refresh 5 minutes before expiry, or immediately if less than 5 min left
  const msUntilRefresh = Math.max(exp - Date.now() - 5 * 60 * 1000, 0);

  proactiveRefreshTimer = setTimeout(async () => {
    const newToken = await doRefresh();
    if (newToken) {
      scheduleProactiveRefresh(newToken);
    }
  }, msUntilRefresh);
};

/**
 * Single-flight refresh: ensures only ONE refresh request is ever in-flight.
 * All concurrent callers share the same promise.
 */
const doRefresh = async () => {
  if (isRefreshing) {
    // Already refreshing — queue this caller and wait
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const newToken = await tryRefreshToken();

    if (newToken) {
      processQueue(null, newToken);
      return newToken;
    }

    // Refresh truly failed — only log out if we have no valid refresh token left
    const remainingRefresh = getStoredRefreshToken();
    if (!remainingRefresh) {
      clearStoredAuth();
      window.dispatchEvent(new Event('auth-change'));
    }
    processQueue(new Error('Refresh failed'));
    return null;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
};

// ─── Main fetch wrapper ────────────────────────────────────────────────────────
export const apiFetch = async (path, options = {}) => {
  let token = getStoredAuthToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(apiUrl(path), { ...options, headers });

  // Not a 401 → return as-is
  if (response.status !== 401) return response;

  // Access token expired → try a silent refresh
  const newToken = await doRefresh();

  if (!newToken) {
    // Refresh failed — return the original 401 so callers can handle gracefully
    return response;
  }

  // Schedule proactive refresh for the new token
  scheduleProactiveRefresh(newToken);

  // Retry the original request with the fresh token
  const retryHeaders = { ...(options.headers || {}), Authorization: `Bearer ${newToken}` };
  return fetch(apiUrl(path), { ...options, headers: retryHeaders });
};

// ─── Bootstrap ─────────────────────────────────────────────────────────────────
// On app start, if there's a valid token, schedule proactive refresh for it
const initialToken = getStoredAuthToken();
if (initialToken) {
  scheduleProactiveRefresh(initialToken);
}
